import { gql, MutationHookOptions, QueryHookOptions, useQuery, useMutation } from '@apollo/client'
import { PAGING_FIELDS } from 'graphql/fragments'
import { useCallback } from 'react'
import { PaginatedEntity } from 'common/types'
import { TUsePaginatedQuery, usePaginatedQuery } from './usePaginatedQuery'

interface SizingGuide {
  id: string
  name: string
  content?: string
  createdAt: string
  updatedAt: string
}

interface SizingGuidesResult {
  sizingGuides: PaginatedEntity<SizingGuide>
}

interface SizingGuideInput {
  name?: string
  content?: string
}

const SIZING_GUIDES_QUERY = gql`
  ${PAGING_FIELDS}
  query SizingGuidesQuery($paging: CursorPaging, $filter: SizingGuideFilter, $sorting: [SizingGuideSort!]) {
    sizingGuides(paging: $paging, filter: $filter, sorting: $sorting) {
      edges {
        node {
          id
          name
          createdAt
        }
      }
      pageInfo {
        ...PagingFields
      }
    }
  }
`

const SIZING_GUIDE_QUERY = gql`
  query SizingGuideQuery($id: ID!) {
    sizingGuide(id: $id) {
      id
      name
      content
      createdAt
      updatedAt
    }
  }
`

const ALL_SIZING_GUIDES_QUERY = gql`
  query AllSizingGuides {
    sizingGuides(paging: { first: 200 }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const SIZING_GUIDES_FOR_SELECT = gql`
  query SizingGuidesForSelect($paging: CursorPaging, $filter: SizingGuideFilter) {
    sizingGuides(paging: $paging, filter: $filter) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const CREATE_SIZING_GUIDE = gql`
  mutation CreateSizingGuide($input: CreateSizingGuideInput!) {
    createOneSizingGuide(input: { sizingGuide: $input }) {
      id
      name
    }
  }
`

const UPDATE_SIZING_GUIDE = gql`
  mutation UpdateSizingGuide($id: ID!, $update: UpdateSizingGuideInput!) {
    updateOneSizingGuide(input: { id: $id, update: $update }) {
      id
      name
      content
    }
  }
`

const DELETE_SIZING_GUIDE = gql`
  mutation DeleteSizingGuide($id: ID!) {
    deleteOneSizingGuide(input: { id: $id }) {
      id
    }
  }
`

function useSizingGuidesQuery(args?: {
  options?: QueryHookOptions
  persist?: boolean
}): TUsePaginatedQuery<SizingGuidesResult> {
  return usePaginatedQuery<SizingGuidesResult, SizingGuide>({
    query: SIZING_GUIDES_QUERY,
    accessor: 'sizingGuides',
    options: args?.options,
    persist: args?.persist ?? false,
    buildFilter: (filter) => {
      const f: Record<string, any> = {}
      if (filter?.name) f.name = { iLike: `%${filter.name}%` }
      return f
    }
  })
}

function useSizingGuideQuery(id: string) {
  return useQuery<{ sizingGuide: SizingGuide }>(SIZING_GUIDE_QUERY, { variables: { id } })
}

function useAllSizingGuidesQuery() {
  return useQuery<SizingGuidesResult>(ALL_SIZING_GUIDES_QUERY, { fetchPolicy: 'cache-and-network' })
}

function useSizingGuidesAll(search?: string) {
  return useQuery<{ sizingGuides: { edges: { node: { id: string; name: string } }[] } }>(SIZING_GUIDES_FOR_SELECT, {
    variables: {
      paging: { first: 50 },
      ...(search ? { filter: { name: { iLike: `%${search}%` } } } : {})
    },
    fetchPolicy: 'network-only'
  })
}

function useCreateSizingGuideMutation(options?: MutationHookOptions) {
  const [createMutation, { loading }] = useMutation(CREATE_SIZING_GUIDE, options)

  const createSizingGuide = useCallback(
    async (input: SizingGuideInput) => {
      try {
        return await createMutation({ variables: { input } })
      } catch (e) {
        console.error(e)
      }
    },
    [createMutation]
  )

  return { createSizingGuide, loading }
}

function useUpdateSizingGuideMutation(options?: MutationHookOptions) {
  const [updateMutation, { loading }] = useMutation(UPDATE_SIZING_GUIDE, options)

  const updateSizingGuide = useCallback(
    async (id: string, update: SizingGuideInput) => {
      try {
        await updateMutation({ variables: { id, update } })
      } catch (e) {
        console.error(e)
      }
    },
    [updateMutation]
  )

  return { updateSizingGuide, loading }
}

function useDeleteSizingGuideMutation(options?: MutationHookOptions) {
  const [deleteMutation, { loading }] = useMutation(DELETE_SIZING_GUIDE, options)

  const deleteSizingGuide = useCallback(
    async (id: string) => {
      try {
        await deleteMutation({ variables: { id } })
      } catch (e) {
        console.error(e)
      }
    },
    [deleteMutation]
  )

  return { deleteSizingGuide, loading }
}

export {
  useSizingGuidesQuery,
  useSizingGuideQuery,
  useAllSizingGuidesQuery,
  useSizingGuidesAll,
  useCreateSizingGuideMutation,
  useUpdateSizingGuideMutation,
  useDeleteSizingGuideMutation
}
export type { SizingGuide, SizingGuideInput }
